import { IInterest, IUsageType } from "api/types";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { TabletDataContext } from "components/Gates/TabletDataGate";
import { TenantDataContext } from "components/Gates/TenantDataGate";

import React, { useContext, useMemo, useState } from "react";

export interface IAppState {
  locationName: string;
  pointsBalance: number;
  tenantId: string;
  availableInterests: IInterest[];
  availableUsageTypes: IUsageType[];
  selectedInterestIdsOnServer: string[];
  selectedInterestIds: string[];
  selectedUsageTypeIdsOnServer: string[];
  selectedUsageTypeIds: string[];
  verdeTenantIds: string[];
}

export const initialAppState: IAppState = {
  locationName: "",
  pointsBalance: 0,
  tenantId: "",
  availableInterests: [],
  availableUsageTypes: [],
  selectedInterestIds: [],
  selectedUsageTypeIds: [],
  selectedInterestIdsOnServer: [],
  selectedUsageTypeIdsOnServer: [],
  verdeTenantIds: [
    "a0e82b00-4dec-4176-9ff2-dd148972536b",
    "42567d60-f11c-46bf-a2a0-172121f9f44a"
  ]
};

export type MutateState = React.Dispatch<React.SetStateAction<IAppState>>;

const AppStateContext = React.createContext<[IAppState, MutateState]>([
  initialAppState,
  // tslint:disable-next-line: no-empty
  () => {}
]);

const AppStateProvider = props => {
  const tenantData = useContext(TenantDataContext);
  const customerData = useContext(CustomerDataContext);

  const availableInterests = tenantData ? tenantData.interests || [] : [];
  const availableUsageTypes = tenantData ? tenantData.usageTypes || [] : [];
  const tenantId = tenantData ? tenantData.id || "" : "";
  const selectedInterestIdsOnServer = customerData
    ? customerData.customer.interestIds || []
    : [];
  const selectedUsageTypeIdsOnServer = customerData
    ? customerData.customer.usageTypeIds || []
    : [];

  const [state, setState] = useState(initialAppState);
  useMemo(() => {
    setState(currentState => ({
      ...currentState,
      tenantId,
      availableInterests,
      availableUsageTypes,
      selectedInterestIdsOnServer,
      selectedUsageTypeIdsOnServer,
      selectedInterestIds: currentState.selectedInterestIds.length
        ? currentState.selectedInterestIds
        : selectedInterestIdsOnServer,
      selectedUsageTypeIds: currentState.selectedUsageTypeIds.length
        ? currentState.selectedUsageTypeIds
        : selectedUsageTypeIdsOnServer
    }));
  }, [
    stringifyIds(availableInterests),
    stringifyIds(availableUsageTypes),
    stringifyIds(selectedInterestIdsOnServer),
    stringifyIds(selectedUsageTypeIdsOnServer)
  ]);

  return (
    <AppStateContext.Provider value={[state, setState]}>
      {props.children}
    </AppStateContext.Provider>
  );
};

const stringifyIds = value => {
  return JSON.stringify(value.map(x => x.id));
};

const useAppState = () => {
  const [appState, setState] = useContext(AppStateContext);
  function setLocationName(locationName: string) {
    setState(state => ({ ...state, locationName }));
  }
  function setPointsBalance(pointsBalance: number) {
    setState(state => ({ ...state, pointsBalance }));
  }

  function setAvailableInterests(availableInterests: IInterest[]) {
    setState(state => ({ ...state, availableInterests }));
    console.log(availableInterests);
  }
  function setAvailableUsageTypes(availableUsageTypes: IUsageType[]) {
    setState(state => ({ ...state, availableUsageTypes }));
    console.log(availableUsageTypes);
  }

  function setSelectedInterestIdsOnServer(
    selectedInterestIdsOnServer: string[]
  ) {
    setState(state => ({ ...state, selectedInterestIdsOnServer }));
  }
  function setSelectedUsageTypeIdsOnServer(
    selectedUsageTypeIdsOnServer: string[]
  ) {
    setState(state => ({ ...state, selectedUsageTypeIdsOnServer }));
  }

  function setSelectedInterestIds(selectedInterestIds: string[]) {
    setState(state => ({ ...state, selectedInterestIds }));
  }
  function setSelectedUsageTypeIds(selectedUsageTypeIds: string[]) {
    setState(state => ({ ...state, selectedUsageTypeIds }));
  }

  function localUsageTypeSelectionChanged(): boolean {
    return !(
      appState.selectedUsageTypeIds.every(e =>
        appState.selectedUsageTypeIdsOnServer.includes(e)
      ) &&
      appState.selectedUsageTypeIdsOnServer.every(e =>
        appState.selectedUsageTypeIds.includes(e)
      )
    );
  }

  function localInterestSelectionChanged(): boolean {
    return !(
      appState.selectedInterestIds.every(e =>
        appState.selectedInterestIdsOnServer.includes(e)
      ) &&
      appState.selectedInterestIdsOnServer.every(e =>
        appState.selectedInterestIds.includes(e)
      )
    );
  }

  function resetAppState() {
    // We don't want to reset the usage types and interests for the tenant that we cached
    setState(state => ({
      ...initialAppState,
      tenantId: state.tenantId,
      availableInterests: state.availableInterests,
      availableUsageTypes: state.availableUsageTypes
    }));
  }
  return {
    ...appState,
    setLocationName,
    setPointsBalance,
    setAvailableInterests,
    setAvailableUsageTypes,
    setSelectedInterestIds,
    setSelectedUsageTypeIds,
    setSelectedInterestIdsOnServer,
    setSelectedUsageTypeIdsOnServer,
    localUsageTypeSelectionChanged,
    localInterestSelectionChanged,
    resetAppState
  };
};

export { AppStateContext, AppStateProvider, useAppState };



// WEBPACK FOOTER //
// ./src/AppState.tsx